import api from './api';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
} from '../types/auth';
import { getAttribution } from '../utils/attribution';

export const authService = {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<{
      user: any;
      tokens: { accessToken: string; refreshToken: string };
    }>('/auth/login', credentials);

    // Transform backend response to match AuthResponse interface
    return {
      user: response.data.user,
      accessToken: response.data.tokens.accessToken,
      refreshToken: response.data.tokens.refreshToken,
    };
  },

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    // Attach how this browser arrived. Added at the service layer, not the form,
    // so every registration path carries it and no future page can forget to.
    // Both fields may be absent, and the backend never requires them.
    const response = await api.post<{
      user: any;
      tokens: { accessToken: string; refreshToken: string };
    }>('/auth/register', { ...data, ...getAttribution() });

    // Transform backend response to match AuthResponse interface
    return {
      user: response.data.user,
      accessToken: response.data.tokens.accessToken,
      refreshToken: response.data.tokens.refreshToken,
    };
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken });
      } catch (error) {
        // Continue with logout even if API call fails
        console.error('Logout API call failed:', error);
      }
    }
    // Clear local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Update the current user's profile (name).
   */
  async updateProfile(data: {
    firstName?: string;
    lastName?: string;
  }): Promise<User> {
    const response = await api.patch<User>('/auth/me', data);
    return response.data;
  },

  /**
   * Change the current user's password.
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/auth/change-password',
      { currentPassword, newPassword }
    );
    return response.data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await api.post<{
      user: any;
      tokens: { accessToken: string; refreshToken: string };
    }>(
      '/auth/refresh',
      { refreshToken }
    );
    return {
      user: response.data.user,
      accessToken: response.data.tokens.accessToken,
      refreshToken: response.data.tokens.refreshToken,
    };
  },

  /**
   * Resend the verification email for a pending account.
   */
  async sendVerificationEmail(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/verification/resend-verification',
      { email }
    );
    return response.data;
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/verification/verify-email',
      { token }
    );
    return response.data;
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/verification/request-password-reset',
      { email }
    );
    return response.data;
  },

  /**
   * Reset password with token
   */
  async resetPassword(
    token: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/verification/reset-password',
      { token, new_password: newPassword }
    );
    return response.data;
  },
};

export default authService;
